import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, watch, type Ref } from 'vue';

import {
  EnumOrganizationConfig,
  EnumOrganizationTerminology,
} from '@/helpers/enums/organization-config.enum';
import type { OrganizationPages } from '@/helpers/interfaces/dynamic-pages.interface';
import type { OrganizationTerminology } from '@/helpers/interfaces/organization.term.interface';
import { pinia } from '@/pinia';
import { axiosI } from '@/plugins';
import type { Media } from '@/submodules/generated_types/types/media';
import { Organization } from '@/submodules/generated_types/types/organization';
import { ApiHandler } from '@/submodules/postman-to-ts/apiHandler';

import { useLocaleStore } from './locale.store';
import { useVuetifyStore } from './vuetify.pinia';

export const useOrganizationStore = defineStore('organization', () => {
  const organization: Ref<Organization | null> = ref(null);
  const organizationId: Ref<number | null> = ref(null);
  const organizationName: Ref<string> = ref('');
  const organizationNameArabic: Ref<string> = ref('');
  const organizationNotFound = ref(false);
  const twoFactorEnabled: Ref<boolean> = ref(false);
  const registrationRequiresApproval: Ref<{
    domain: string;
    strategy: string;
  } | null> = ref(null);
  const allowAnonymousCourseView: Ref<boolean> = ref(false);
  const allowAnonymousExternalCourseView: Ref<boolean> = ref(false);
  const allowAnonymousEventView: Ref<boolean> = ref(false);
  const certificatesEnabled: Ref<boolean> = ref(false);
  const internalTeamMode: Ref<boolean> = ref(false);
  const onboardingJourneyEnabled: Ref<boolean> = ref(false);
  const passingCriteriaConfigurationEnabledForAdmins: Ref<boolean> = ref(false);
  const passingCriteriaConfigurationEnabledForInstructors: Ref<boolean> =
    ref(false);
  const organizationPages: Ref<OrganizationPages | null> = ref(null);
  const moyasarEnabled: Ref<boolean> = ref(false);
  const tabbyEnabled: Ref<boolean> = ref(false);
  const tamaraEnabled: Ref<boolean> = ref(false);
  const msegatEnabled: Ref<boolean> = ref(false);
  const clarityProjectId: Ref<string> = ref('');
  const organizationTerminology: Ref<
    Record<string, OrganizationTerminology[]>
  > = ref({});
  const loginPageImage: Ref<string> = ref('');
  const loginOrganizationLogo: Ref<string> = ref('');
  const courseBannerImage: Ref<string> = ref('');
  const eventBannerImage: Ref<string> = ref('');
  const courseBannerTitle: Ref<string> = ref('');
  const courseBannerSubtitle: Ref<string> = ref('');
  const eventBannerTitle: Ref<string> = ref('');
  const eventBannerSubtitle: Ref<string> = ref('');
  const externalCourseBannerImage: Ref<string> = ref('');
  const externalCourseBannerTitle: Ref<string> = ref('');
  const externalCourseBannerSubtitle: Ref<string> = ref('');

  const studentText: Ref<string> = ref('');
  const studentsText: Ref<string> = ref('');
  const moduleText: Ref<string> = ref('');
  const modulesText: Ref<string> = ref('');
  const instructorText: Ref<string> = ref('');
  const instructorsText: Ref<string> = ref('');
  const courseText: Ref<string> = ref('');
  const coursesText: Ref<string> = ref('');
  const blendedText: Ref<string> = ref('');
  const dashboardText: Ref<string> = ref('');
  const enrollmentTermAndConditions: Ref<string> = ref('');
  const grantingPhotoUsagePermissions: Ref<string> = ref('');
  const externalCoursesAccreditationNotice: Ref<string> = ref('');
  const callOrganization = async () => {
    try {
      const { path, method } = ApiHandler.auth.getOrganization;
      const { data } = await axiosI<Organization>(path(), {
        method,
      });
      organization.value = data;
      // Fallback brand source — the slim /url payload may predate the
      // name fields; the full payload always carries them.
      organizationName.value = organizationName.value || data.name || '';
      organizationNameArabic.value =
        organizationNameArabic.value || data.nameArabic || '';
      setOrganizationConfig();
    } catch {
      // swallow — organization fetch failed, UI handles missing state
    }
  };
  const getOrganizationUsingUrl = async () => {
    try {
      organizationNotFound.value = false;
      const { path, method } = ApiHandler.organizations.getIdFromUrl;
      const { data } = await axiosI<{
        id: number;
        name?: string;
        nameArabic?: string | null;
        terminology: Record<string, OrganizationTerminology[]>;
      }>(path(), {
        method,
      });
      localStorage.setItem('Organization', data.id.toString());
      organizationId.value = data.id;
      organizationName.value = data.name || organizationName.value;
      organizationNameArabic.value =
        data.nameArabic || organizationNameArabic.value;
      organizationTerminology.value = data.terminology;
      setOrganizationTerms();
      return data;
    } catch (error) {
      organizationNotFound.value = true;
    }
  };

  /**
   * Tenant-aware browser-tab titles (KP-53).
   *
   * Route meta names are baked at build time as
   * `${import.meta.env.VITE_DEFAULT_TITLE} | <Page>` — one hardcoded brand
   * for every tenant, so e.g. the Watar portal's tab read "Himam | Login".
   * `setTabTitle` swaps that brand segment for the organization's actual
   * name (Arabic UI prefers the Arabic name) and falls back to the
   * build-time brand when the organization has no name. Unbranded titles
   * pass through unchanged; re-applying is idempotent.
   */
  const defaultTabBrand = (import.meta.env.VITE_DEFAULT_TITLE as string) || '';
  const tabBrand = computed(() => {
    const isArabic = useLocaleStore(pinia).locale === 'ar';
    const preferred = isArabic
      ? organizationNameArabic.value || organizationName.value
      : organizationName.value || organizationNameArabic.value;
    return preferred || defaultTabBrand;
  });
  const setTabTitle = (title?: string) => {
    const brand = tabBrand.value;
    const knownBrands = [
      defaultTabBrand,
      organizationName.value,
      organizationNameArabic.value,
    ];
    // No page segment (route without a meta name) -> bare brand, matching
    // the old `document.title = VITE_DEFAULT_TITLE` behavior.
    let next = brand;
    if (title) {
      next = title;
      for (const known of knownBrands) {
        if (!known) continue;
        if (title === known) {
          next = brand;
          break;
        }
        if (title.startsWith(`${known} | `)) {
          next = `${brand} | ${title.slice(known.length + 3)}`;
          break;
        }
      }
    }
    if (document.title !== next) document.title = next;
  };
  // Re-apply when the brand settles or changes (org payloads resolving
  // after the first navigation, or a locale switch flipping the name).
  watch(tabBrand, () => setTabTitle(document.title || undefined));
  const setOrganizationConfig = () => {
    organization.value?.config.forEach((config) => {
      switch (config.configurationKey.key) {
        case EnumOrganizationConfig.TWO_FACTOR_ENABLED:
          twoFactorEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.REGISTRATION_REQUIRES_APPROVAL:
          registrationRequiresApproval.value = {
            domain:
              (
                config.value as {
                  domain: string;
                  strategy: string;
                }
              )?.domain || '',
            strategy:
              (
                config.value as {
                  domain: string;
                  strategy: string;
                }
              )?.strategy || '',
          };
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_COURSE_VIEW:
          allowAnonymousCourseView.value = !!config.value;
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_EXTERNAL_COURSE_VIEW:
          allowAnonymousExternalCourseView.value = !!config.value;
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_EVENT_VIEW:
          allowAnonymousEventView.value = !!config.value;
          break;
        case EnumOrganizationConfig.CERTIFICATES_ENABLED:
          certificatesEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.INTERNAL_TEAM_MODE:
          internalTeamMode.value = !!config.value;
          break;
        case EnumOrganizationConfig.ONBOARDING_JOURNEY:
          onboardingJourneyEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.PASSING_CRITERIA_CONFIGURATION_ENABLED_FOR_ADMINS:
          passingCriteriaConfigurationEnabledForAdmins.value = !!config.value;
          break;
        case EnumOrganizationConfig.PASSING_CRITERIA_CONFIGURATION_ENABLED_FOR_INSTRUCTORS:
          passingCriteriaConfigurationEnabledForInstructors.value =
            !!config.value;
          break;
        case EnumOrganizationConfig.MOYASAR_CREDENTIAL_CONFIGURATION:
          moyasarEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.TABBY_CREDENTIAL_CONFIGURATION:
          tabbyEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.TAMARA_CREDENTIAL_CONFIGURATION:
          tamaraEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.MSEGAT_CREDENTIAL_CONFIGURATION:
          msegatEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.CLARITY_PROJECT_ID:
          clarityProjectId.value =
            (
              config.value as {
                project_id: string;
              }
            )?.project_id || '';
          break;
      }
    });
  };
  const getOrganizationTerminology = async () => {
    try {
      const { path, method } =
        ApiHandler.organizations.getOrganizationsTerminology;
      const { data } = await axiosI<Record<string, OrganizationTerminology[]>>(
        path(),
        { method }
      );
      organizationTerminology.value = data;
      setOrganizationTerms();
      return data;
    } catch (error) {
      return error;
    }
  };
  const getMediaValue: (
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => Media | undefined | null = (
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => {
    const isDark = localStorage.getItem('theme') === 'myCustomDarkTheme';
    const locale = localeKey || (useLocaleStore(pinia).locale as 'ar' | 'en');
    if (term?.defaultValue?.isRequired) {
      let value: Media | undefined | null = undefined;
      term.organizationTerms?.forEach((termKey) => {
        if (termKey.language == locale) {
          value = termKey.medium || term.defaultValue.media || undefined;
        }
      });
      return value || undefined;
    } else {
      if (term?.organizationTerms?.length) {
        let value: Media | undefined | null = undefined;
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale) {
            value = termKey.medium || undefined;
          }
        });
        return value || undefined;
      }
      // special case for login organization logo
      else if (
        term?.slug === EnumOrganizationTerminology.LOGIN_ORGANIZATION_LOGO
      ) {
        return isDark
          ? (organization.value?.darkLogo ?? organization.value?.logo)
          : organization.value?.logo;
      } else return term?.defaultValue.media || undefined;
    }
  };
  const getValue: (
    isSingular: boolean,
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => string = (
    isSingular: boolean,
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => {
    const locale = localeKey || (useLocaleStore(pinia).locale as 'ar' | 'en');
    if (term?.defaultValue?.isRequired) {
      if (!term.organizationTerms?.length) {
        return (
          (isSingular
            ? term.defaultValue[locale].singular
            : term.defaultValue[locale].plural) || ''
        );
      } else {
        let value = '';
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale) {
            value =
              (isSingular ? termKey.value.singular : termKey.value.plural) ||
              (isSingular
                ? term.defaultValue[locale].singular
                : term.defaultValue[locale].plural) ||
              '';
          }
        });
        return value;
      }
    } else {
      if (term?.organizationTerms?.length) {
        let value = '';
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale)
            value =
              (isSingular ? termKey.value.singular : termKey.value.plural) ||
              '';
        });
        return value;
      } else
        return (
          (isSingular
            ? term?.defaultValue[locale].singular
            : term?.defaultValue[locale].plural) || ''
        );
    }
  };
  const setOrganizationTerms = () => {
    /** >>>>>>>>>>>>>>> Login Page Image <<<<<<<<<<<<<<< **/
    loginPageImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.AUTH_IMAGE
        ]?.[0]
      )?.url || '';
    loginOrganizationLogo.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.LOGIN_ORGANIZATION_LOGO
        ]?.[0]
      )?.url || '';

    /** >>>>>>>>>>>>>>> External Course Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    externalCourseBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title

    externalCourseBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    externalCourseBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Course Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    courseBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title

    courseBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    courseBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Event Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    eventBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title
    eventBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    eventBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Text Keys <<<<<<<<<<<<<<< **/
    //>>>>>>> Blended
    blendedText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.BLENDED]?.[0]
      ) || '';
    //>>>>>>> Course
    courseText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.COURSE]?.[0]
      ) || '';
    coursesText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.COURSE]?.[0]
      ) || '';
    //>>>>>>> Dashboard
    dashboardText.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.DASHBOARD
        ]?.[0]
      ) || '';
    //>>>>>>> Instructor
    instructorText.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.INSTRUCTOR
        ]?.[0]
      ) || '';
    instructorsText.value =
      getValue(
        false,
        organizationTerminology.value[
          EnumOrganizationTerminology.INSTRUCTOR
        ]?.[0]
      ) || '';
    //>>>>>>> Module
    moduleText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.MODULE]?.[0]
      ) || '';
    modulesText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.MODULE]?.[0]
      ) || '';
    //>>>>>>> Student
    studentText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.STUDENT]?.[0]
      ) || '';
    studentsText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.STUDENT]?.[0]
      ) || '';

    enrollmentTermAndConditions.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.ENROLLMENT_TERMS_AND_CONDITIONS
        ]?.[0]
      ) || '';
    grantingPhotoUsagePermissions.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.GRANTING_PHOTO_USAGE_PERMISSIONS
        ]?.[0]
      ) || '';
    externalCoursesAccreditationNotice.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSES_ACCREDITATION_NOTICE
        ]?.[0]
      ) || '';
  };
  /**
   * Terminology-driven labels (dashboardText, coursesText, banner titles,
   * media …) are plain refs snapshotted by setOrganizationTerms() with
   * whatever locale is current at call time. During app bootstrap,
   * initializeI18n() loads the terminology and settles the locale
   * concurrently (changeLocale is called with setTerms=false), so a
   * snapshot taken before the locale switch would otherwise stick until
   * the next manual language toggle or reload (KP-30). Re-derive whenever
   * the locale settles or changes.
   */
  watch(
    () => useLocaleStore(pinia).locale,
    () => setOrganizationTerms()
  );

  /**
   * The tab icon has the same boot-order hazard: initializeI18n() applies
   * organization.icon before the AUTHED org payload (the only one carrying
   * icon) has loaded on a fresh session, so the static default stuck for
   * the whole session. Re-apply whenever the org's icon URL arrives/changes.
   */
  watch(
    () => organization.value?.icon?.url,
    (iconUrl) => {
      if (iconUrl) useVuetifyStore(pinia).changeFavoriteIcon(iconUrl);
    }
  );
  const getOrganizationDynamicPages = async () => {
    try {
      const { path, method } = ApiHandler.dynamicPages.getOrganizationPages;
      const { data } = await axiosI<OrganizationPages>(path(), {
        method,
      });
      organizationPages.value = data;
      return data;
    } catch (error) {
      return error as AxiosError;
    }
  };
  return {
    organization,
    getOrganizationDynamicPages,
    organizationPages,
    organizationId,
    organizationName,
    organizationNameArabic,
    setTabTitle,
    callOrganization,
    getOrganizationUsingUrl,
    twoFactorEnabled,
    registrationRequiresApproval,
    allowAnonymousCourseView,
    allowAnonymousExternalCourseView,
    allowAnonymousEventView,
    certificatesEnabled,
    internalTeamMode,
    onboardingJourneyEnabled,
    passingCriteriaConfigurationEnabledForAdmins,
    passingCriteriaConfigurationEnabledForInstructors,
    moyasarEnabled,
    tabbyEnabled,
    tamaraEnabled,
    msegatEnabled,
    clarityProjectId,
    getOrganizationTerminology,
    loginPageImage,
    courseBannerImage,
    eventBannerImage,
    courseBannerTitle,
    courseBannerSubtitle,
    eventBannerTitle,
    eventBannerSubtitle,
    studentText,
    studentsText,
    moduleText,
    modulesText,
    instructorText,
    instructorsText,
    courseText,
    coursesText,
    blendedText,
    dashboardText,
    setOrganizationTerms,
    loginOrganizationLogo,
    enrollmentTermAndConditions,
    grantingPhotoUsagePermissions,
    externalCoursesAccreditationNotice,
    externalCourseBannerImage,
    externalCourseBannerSubtitle,
    externalCourseBannerTitle,
    organizationNotFound,
  };
});
